'use client';

import { AlertCircle, ArrowLeft, ArrowRight, CheckCircle2, Loader2, LogIn, Mail, User } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { dispatchLogin } from '@/features/auth/store/actions';
import { setPortalLoginLookup } from '@/features/portal/store';
import { portalSetLoginRemember } from '@/features/portal/store/actions';
import { Button } from '@/shared/components/ui/common/Button';
import { formatCpfCnpj } from '@/shared/formatters/document';
import { isValidEmail } from '@/shared/utils/contato';
import { destinoSentencaPortal } from '@/shared/utils/destinoSentencaPortal';
import { loadRecentPortalLoginIdentifier, saveRecentPortalLoginIdentifier } from '@/shared/utils/recentPortalLoginIdentifier';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useAuth } from '../contexts/AuthContext';
import { buildDocumentoNaoEncontradoMessage, detectLoginIdentifierType, isDocumentoType, isLoginIdentifierValid, looksLikeDocumento, toSubmittedLoginIdentifier } from '../utils/loginIdentifier';
type Step = 'identifier' | 'name';
const LEGACY_REMEMBER_KEY = 'arb_login_identifier';
const extractInviteToken = (redirect: string | null): string | undefined => {
  if (!redirect) return undefined;
  return redirect.match(/^\/convite\/([^/?#]+)/)?.[1];
};
export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const {
    user,
    isAuthenticated
  } = useAppSelector(state => state.auth);
  const redirect = searchParams.get('redirect');
  const inviteToken = extractInviteToken(redirect);
  const emailOnly = Boolean(inviteToken);
  const needsProfileCompletion = isAuthenticated && user && (!user.nomeCompleto || user.nomeCompleto.trim() === '');
  const [step, setStep] = useState<Step>('identifier');
  const [identifier, setIdentifier] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const {
    completeProfile,
    error
  } = useAuth();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(LEGACY_REMEMBER_KEY);
    const recentIdentifier = loadRecentPortalLoginIdentifier();
    if (recentIdentifier) {
      setIdentifier(looksLikeDocumento(recentIdentifier) ? formatCpfCnpj(recentIdentifier) : recentIdentifier);
    }
  }, []);
  useEffect(() => {
    if (needsProfileCompletion) {
      setStep('name');
    }
  }, [needsProfileCompletion]);
  const isInputValid = emailOnly ? isValidEmail(identifier.trim()) : isLoginIdentifierValid(identifier);
  const hasInputError = identifier.length > 0 && !isInputValid && !isLoading;
  const handleIdentifierChange = (raw: string) => {
    if (!emailOnly && looksLikeDocumento(raw)) {
      setIdentifier(formatCpfCnpj(raw));
    } else {
      setIdentifier(raw);
    }
    if (message) setMessage(null);
  };
  const handleSubmitIdentifier = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!isInputValid) return;
    setIsLoading(true);
    setMessage(null);
    const trimmed = identifier.trim();
    const type = detectLoginIdentifierType(trimmed);
    const submittedIdentifier = toSubmittedLoginIdentifier(trimmed);
    try {
      const result = await dispatch(dispatchLogin({
        identifier: submittedIdentifier,
        inviteToken
      }));
      if (!dispatchLogin.fulfilled.match(result)) {
        setMessage({
          type: 'error',
          text: result.payload ?? 'Erro ao consultar acesso.'
        });
        return;
      }
      const payload = result.payload;
      if (payload.kind === 'regular') {
        const codigoParams = new URLSearchParams({
          email: payload.email
        });
        if (redirect) codigoParams.set('redirect', redirect);
        router.push(`/login/codigo?${codigoParams.toString()}`);
        return;
      }
      if (payload.kind === 'portal') {
        const hasNotificadoChannels = (payload.channels?.length ?? 0) > 0;
        if (isDocumentoType(type) && !hasNotificadoChannels) {
          setMessage({
            type: 'error',
            text: buildDocumentoNaoEncontradoMessage(type)
          });
          return;
        }
        dispatch(setPortalLoginLookup({
          identifier: submittedIdentifier,
          redirectTo: destinoSentencaPortal(redirect),
          nome: payload.nome,
          channels: payload.channels ?? []
        }));
        saveRecentPortalLoginIdentifier(submittedIdentifier);
        if (rememberMe) {
          await dispatch(portalSetLoginRemember({
            identifier: submittedIdentifier
          }));
        }
        router.push('/login/canal');
        return;
      }
      if (payload.kind === 'inactive') {
        setMessage({
          type: 'error',
          text: payload.message
        });
        return;
      }
      setMessage({
        type: 'error',
        text: isDocumentoType(type) ? buildDocumentoNaoEncontradoMessage(type) : 'Não encontramos uma conta com esse e-mail.'
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text: (err as Error).message
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleCompleteName = async (e_0: React.FormEvent) => {
    e_0.preventDefault();
    setIsLoading(true);
    setMessage(null);
    try {
      await completeProfile(nomeCompleto);
    } catch (err_0) {
      setMessage({
        type: 'error',
        text: (err_0 as Error).message
      });
    } finally {
      setIsLoading(false);
    }
  };
  if (step === 'identifier') {
    const errorText = hasInputError ? emailOnly ? 'E-mail inválido.' : 'CPF, CNPJ ou e-mail inválido.' : message?.type === 'error' ? message.text : error ?? null;
    const canSubmit = isInputValid && !isLoading;
    return <form onSubmit={handleSubmitIdentifier} className="flex w-full flex-col gap-8">
        <div className="flex flex-col gap-3">
          <h1 className="text-2xl leading-tight font-semibold tracking-tight text-neutral-900 sm:text-3xl">
            Acesse sua conta
          </h1>
          <p className="text-base leading-relaxed text-neutral-600">
            Confirme sua identidade e acesse a plataforma.
          </p>
        </div>

        <div className="flex w-full flex-col gap-5">
          <div className="flex flex-col gap-2">
            <label htmlFor="login-identifier" className="text-sm font-medium text-neutral-800">
              {emailOnly ? 'E-mail' : 'CPF, CNPJ ou e-mail'}
            </label>
            <div className={`flex h-12 items-center gap-2 rounded-[8px] border bg-white px-3.5 shadow-sm transition-colors focus-within:ring-2 focus-within:ring-offset-2 ${hasInputError ? 'border-error-500/60 focus-within:border-error-500 focus-within:ring-error-200' : 'focus-within:border-brand-700 focus-within:ring-brand-100 border-neutral-300'}`}>
              <input id="login-identifier" type={emailOnly ? 'email' : 'text'} inputMode={emailOnly ? 'email' : 'text'} autoComplete={emailOnly ? 'email' : 'username'} value={identifier} onChange={e_1 => handleIdentifierChange(e_1.target.value)} placeholder={emailOnly ? 'Informe seu e-mail' : 'Informe seu CPF, CNPJ ou e-mail'} className="min-w-0 flex-1 bg-transparent text-base text-neutral-900 placeholder:text-neutral-500 focus:outline-none" aria-invalid={Boolean(errorText)} aria-describedby={errorText ? 'login-identifier-error' : undefined} autoFocus />
              {hasInputError && <AlertCircle className="text-error-500 size-4" />}
            </div>
            {errorText && <p id="login-identifier-error" role="alert" className="text-error-700 text-sm leading-relaxed">
                {errorText}
              </p>}
          </div>

          <button type="button" onClick={() => setRememberMe(v => !v)} role="switch" aria-checked={rememberMe} className="focus-visible:ring-brand-500 flex min-h-8 items-center gap-2.5 self-start rounded-[8px] text-left focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:outline-none">
            <span className={`flex h-5 w-9 items-center rounded-full p-0.5 transition-colors ${rememberMe ? 'bg-brand-700 justify-end' : 'justify-start bg-neutral-200'}`}>
              <span className="size-4 rounded-full bg-white shadow-[0px_1px_3px_rgba(10,13,18,0.1)]" />
            </span>
            <span className="text-sm text-neutral-600">Lembrar para o próximo acesso</span>
          </button>
        </div>

        <button type="submit" disabled={!canSubmit} aria-busy={isLoading} className={`focus-visible:ring-brand-500 flex h-12 w-full items-center justify-center gap-2 rounded-[8px] px-4 text-sm font-semibold shadow-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${canSubmit ? 'bg-brand-700 hover:bg-brand-600 cursor-pointer text-white' : 'cursor-not-allowed border border-neutral-200 bg-neutral-100 text-neutral-500'}`}>
          {isLoading ? <>
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              <span role="status">Continuando...</span>
            </> : <>
              Continuar
              <ArrowRight className="size-4" aria-hidden="true" />
            </>}
        </button>
      </form>;
  }
  return <div className="animate-fade-in w-full space-y-6 text-left">
      <div className="space-y-6">
        {!needsProfileCompletion && <Button variant="neutral-tertiary" size="small" icon={<ArrowLeft className="h-4 w-4" />} onClick={() => {
        setStep('identifier');
        setNomeCompleto('');
        setMessage(null);
      }} className="-ml-2 text-neutral-500 hover:text-neutral-900">
            Voltar
          </Button>}

        <div className="space-y-2">
          <p className="text-brand-600 text-[10px] font-bold tracking-widest uppercase">
            Complete seu cadastro
          </p>
          <div className="flex items-center justify-start gap-3">
            <div className="bg-brand-50 text-brand-600 flex h-8 w-8 flex-none items-center justify-center rounded-lg">
              <Mail className="h-4 w-4" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-neutral-900">
              Qual é o seu nome?
            </h2>
          </div>
          <p className="text-sm text-neutral-600">
            Precisamos do seu nome completo para criar sua conta
          </p>
        </div>
      </div>

      <form onSubmit={handleCompleteName} className="space-y-6">
        <div className="flex flex-col gap-2">
          <label htmlFor="nomeCompleto" className="text-sm font-medium text-neutral-800">
            Nome completo
          </label>
          <div className="flex h-11 items-center gap-2 rounded-[8px] border border-neutral-200 bg-white px-3 shadow-[0px_1px_1px_rgba(24,29,39,0.08)]">
            <User className="size-4 text-neutral-500" />
            <input id="nomeCompleto" type="text" value={nomeCompleto} onChange={e_2 => setNomeCompleto(e_2.target.value)} required placeholder="Seu nome completo" autoFocus className="min-w-0 flex-1 bg-transparent text-base text-[--color-text-heading-dark] placeholder:text-neutral-500 focus:outline-none" />
          </div>
        </div>

        {(message || error) && <div className={`flex items-start gap-3 rounded-xl border p-3 text-sm transition-all ${message?.type === 'success' ? 'border-success-100 bg-success-50 text-success-800' : 'border-error-100 bg-error-50 text-error-800'}`}>
            {message?.type === 'success' ? <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" /> : <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />}
            <div className="flex-1 text-left">
              <p className="font-medium">{message?.text || error}</p>
            </div>
          </div>}

        <Button type="submit" disabled={isLoading || !nomeCompleto || nomeCompleto.length < 3} loading={isLoading} className="w-full" variant={!nomeCompleto || nomeCompleto.length < 3 ? 'brand-gradient-inactive' : 'brand-gradient'} size="large" icon={<LogIn className="h-5 w-5" />}>
          Continuar
        </Button>
      </form>
    </div>;
}